import { FC, useMemo } from 'react';
import { Field, FormSpy } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FormControl, FormGroup, FormLabel } from '@mui/material';

import FormCheckbox from '@components/FormCheckbox/FormCheckbox';
import FormSelect from '@components/FormSelect/FormSelect';
import { userDataSelector } from '@store/features/auth/user/selectors';
import useAthleteGenders from '@store/hooks/useAthleteGenders';
import useAthleteRoles from '@store/hooks/useAthleteRoles';
import useAthleteSports from '@store/hooks/useAthleteSports';

import { IUserProfileNormalFormProps } from './UserProfileNormalForm.interface';

import './style.scss';

const UserProfileNormalForm: FC<IUserProfileNormalFormProps> = ({
  handleSubmit,
  setIsFormDirty,
}) => {
  const { t } = useTranslation();

  const user = useSelector(userDataSelector);
  const athleteGenders = useAthleteGenders();
  const athleteRoles = useAthleteRoles();
  const athleteSports = useAthleteSports();
  const genderOptions = useMemo(
    () => [{ id: '', name: t('common.none') }, ...athleteGenders],
    [t, athleteGenders],
  );

  return (
    <form
      className="user-profile-normal-form"
      id="user-profile-normal-form"
      onSubmit={handleSubmit}
      noValidate
    >
      {(user?.showGender || user?.showRole) && (
        <div className="user-profile-normal-form__inputs">
          <p className="user-profile-normal-form__required small">{t('common.required')}</p>

          {user?.showGender && (
            <Field
              component={FormSelect}
              className="user-profile-normal-form__input"
              label={t('userProfile.gender')}
              name="genderId"
              options={genderOptions}
            />
          )}

          {user?.showRole && (
            <Field
              component={FormSelect}
              className="user-profile-normal-form__input"
              label={t('userProfile.role')}
              name="roleId"
              options={athleteRoles}
              required={user?.showRole}
            />
          )}
        </div>
      )}

      {user?.showSports && (
        <div className="user-profile-normal-form__checkboxes">
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend" className="user-profile-normal-form__checkboxes-legend">
              {t('userProfile.normalSportsInvolved')}
              <span className="user-profile-normal-form__asterisk">*</span>
            </FormLabel>
            <FormGroup className="user-profile-normal-form__checkboxes-list">
              {athleteSports.map(item => (
                <Field
                  component={FormCheckbox}
                  type="checkbox"
                  className="user-profile-normal-form__checkboxes-item"
                  name="sportIds"
                  value={item.id}
                  label={item.name}
                  key={item.id}
                />
              ))}
            </FormGroup>
          </FormControl>
        </div>
      )}

      <FormSpy subscription={{ dirty: true }} onChange={({ dirty }) => setIsFormDirty(dirty)} />
    </form>
  );
};

export default UserProfileNormalForm;
